import React, { createContext, useContext, useLayoutEffect, useState } from 'react';
import type { ReactNode, RefObject } from 'react';

import { expVal } from '@confluence/feature-experiments';

import {
	DEFAULT_ANALYTICS_CARD_PLACEMENT,
	getDialogBesideCardPlacement,
	getOpenAnalyticsDialogCardPopups,
	pinOpenCardsBesideDialog,
	type AnalyticsCardPlacement,
} from './analyticsDialogCardPlacement.ts';

type DialogRect = Pick<DOMRect, 'left' | 'top' | 'width' | 'height'>;

type PlacementLayout = {
	dialogRect: DialogRect | null;
	epoch: number;
};

type AnalyticsDialogCardPlacementProviderProps = {
	dialogRef: RefObject<HTMLElement | null>;
	children: ReactNode;
};

const AnalyticsDialogCardPlacementContext = createContext<PlacementLayout>({
	dialogRect: null,
	epoch: 0,
});

const toDialogRect = (node: HTMLElement | null): DialogRect | null => {
	if (!node) {
		return null;
	}
	const { left, top, width, height } = node.getBoundingClientRect();
	return { left, top, width, height };
};

const isSameDialogRect = (left: DialogRect | null, right: DialogRect | null): boolean =>
	left === right ||
	(left != null &&
		right != null &&
		left.left === right.left &&
		left.top === right.top &&
		left.width === right.width &&
		left.height === right.height);

export const AnalyticsDialogCardPlacementProvider = ({
	dialogRef,
	children,
}: AnalyticsDialogCardPlacementProviderProps): React.JSX.Element => {
	const [layout, setLayout] = useState<PlacementLayout>({
		dialogRect: null,
		epoch: 0,
	});

	useLayoutEffect(() => {
		if (!expVal('ai_agent_analytics_cards', 'isEnabled', false)) {
			return;
		}

		let frameId = 0;
		let popupResizeObserver: ResizeObserver | null = null;
		let popupAttributeObserver: MutationObserver | null = null;
		const pinCards = () => {
			pinOpenCardsBesideDialog(dialogRef.current);
			if (popupResizeObserver || popupAttributeObserver) {
				for (const popup of getOpenAnalyticsDialogCardPopups(dialogRef.current)) {
					popupResizeObserver?.observe(popup);
					popupAttributeObserver?.observe(popup, {
						attributes: true,
						attributeFilter: ['style', 'class'],
					});
				}
			}
		};
		popupResizeObserver =
			typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(pinCards);
		popupAttributeObserver =
			typeof MutationObserver === 'undefined' ? null : new MutationObserver(pinCards);
		const updateLayoutAndPin = () => {
			const nextRect = toDialogRect(dialogRef.current);
			setLayout((current) => ({
				dialogRect: isSameDialogRect(current.dialogRect, nextRect) ? current.dialogRect : nextRect,
				epoch: current.epoch + 1,
			}));
			pinCards();
		};
		const scheduleUpdate = () => {
			if (frameId) {
				return;
			}
			frameId = requestAnimationFrame(() => {
				frameId = 0;
				updateLayoutAndPin();
			});
		};

		const viewport = typeof visualViewport === 'undefined' ? null : visualViewport;

		updateLayoutAndPin();
		window.addEventListener('scroll', scheduleUpdate, true);
		window.addEventListener('resize', scheduleUpdate);
		viewport?.addEventListener('scroll', scheduleUpdate);
		viewport?.addEventListener('resize', scheduleUpdate);

		const dialog = dialogRef.current;
		const resizeObserver =
			typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate);
		if (dialog) {
			resizeObserver?.observe(dialog);
		}

		const mutationObserver =
			typeof MutationObserver === 'undefined' ? null : new MutationObserver(pinCards);
		if (typeof document !== 'undefined') {
			mutationObserver?.observe(document.body, {
				childList: true,
				subtree: true,
			});
		}
		if (dialog) {
			mutationObserver?.observe(dialog, {
				attributes: true,
				subtree: true,
				attributeFilter: ['aria-expanded', 'aria-controls'],
			});
		}

		return () => {
			if (frameId) {
				cancelAnimationFrame(frameId);
			}
			window.removeEventListener('scroll', scheduleUpdate, true);
			window.removeEventListener('resize', scheduleUpdate);
			viewport?.removeEventListener('scroll', scheduleUpdate);
			viewport?.removeEventListener('resize', scheduleUpdate);
			resizeObserver?.disconnect();
			mutationObserver?.disconnect();
			popupResizeObserver?.disconnect();
			popupAttributeObserver?.disconnect();
		};
	}, [dialogRef]);

	return (
		<AnalyticsDialogCardPlacementContext.Provider value={layout}>
			{children}
		</AnalyticsDialogCardPlacementContext.Provider>
	);
};

export const useAnalyticsDialogCardPlacement = (
	triggerRef: RefObject<HTMLElement | null>,
): AnalyticsCardPlacement => {
	const { dialogRect, epoch } = useContext(AnalyticsDialogCardPlacementContext);

	useLayoutEffect(() => {
		if (!expVal('ai_agent_analytics_cards', 'isEnabled', false)) {
			return;
		}

		pinOpenCardsBesideDialog(triggerRef.current?.closest('[role="dialog"]') ?? null);
	}, [dialogRect, epoch, triggerRef]);

	if (!expVal('ai_agent_analytics_cards', 'isEnabled', false)) {
		return DEFAULT_ANALYTICS_CARD_PLACEMENT;
	}

	return getDialogBesideCardPlacement(
		dialogRect,
		typeof document !== 'undefined' ? triggerRef.current?.getBoundingClientRect() : undefined,
	);
};
